import { Message } from '@arco-design/web-vue';
import { jsonParse } from '@bigo/helpers';
import axios from 'axios';
import { getToken } from '@/common/utils';
import i18n from '@/i18n';
import { blobToText, createEnum } from '@/libs/utils';
import { useAppStore } from '@/store/app';
import { STORAGE_KEY } from '@/constants/storage';

export class HttpResponseError extends Error {
  constructor(response, json) {
    super(json.message);
    this.response = response;
    this.json = json;
  }

  match(code) {
    return this.json.code === code;
  }
}

const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** @enum {number} */
const ResponseCode = createEnum({
  /** 成功 */
  Successfully: 0,
  /** TOKEN 失效（权限变更等导致） */
  TokenExpired: 20002,
  /** TOKEN 错误 */
  Unauthorized: 20003,
  /** 账号被禁用 */
  AccountDisabled: 20006,
});

async function parseResponseBody(response) {
  if (response.config.responseType === 'blob') {
    // 如果实际返回格式为json，则将blob重新转换为json
    if (response.data.type === 'application/json') {
      const json = await blobToText(response.data);
      return { json: jsonParse(json) };
    }

    return { blob: response.data };
  }

  return { json: response.data };
}

export const Silent = Symbol('Silent');

export function showResponseError(url, body) {
  const { code, message } = body || {};

  const localeMessage = i18n.global.t(`${url}.${code}`, '?');
  if (localeMessage !== '?') Message.error(localeMessage);
  else if (message) Message.error(message);
}

http.interceptors.request.use(
  (config) =>
    getToken().then(
      (token) => {
        const deviceId = localStorage.getItem(STORAGE_KEY.DEVICE_ID);
        config.headers['X-Auth-Token'] = token;
        config.headers['bigo-device-id'] = deviceId;
        return config;
      },
      (error) => Promise.reject(error),
    ),
  (error) => Promise.reject(error),
);

http.interceptors.response.use(
  async (response) => {
    const appStore = useAppStore();
    const { json, blob } = await parseResponseBody(response);
    if (blob) return blob;

    switch (json.code) {
      case ResponseCode.Successfully:
        return json.data;

      case ResponseCode.TokenExpired:
      case ResponseCode.Unauthorized:
      case ResponseCode.AccountDisabled:
        appStore.setTokenErrCnt();
        return Promise.reject(new HttpResponseError(response, json));

      default:
        if (!response.config[Silent]) showResponseError(response.config.url, json);
        return Promise.reject(new HttpResponseError(response, json));
    }
  },
  (error) => {
    if (error.response?.status === 413) {
      Message.error(i18n.global.t('lang158'));
    } else if (error.message) {
      Message.error(error.message);
    }

    throw error;
  },
);

export default http;
